import axios from "axios";

const GAS_FEE_BASE_URL = process.env.REACT_APP_GAS_FEE_API_URL;
const ADMIN_WALLET_ADDRESS = process.env.REACT_APP_ADMIN_WALLET_ADDRESS;

const gasFeeAxios = axios.create({
  baseURL: GAS_FEE_BASE_URL,
});

let adminToken = null;
let adminTokenExpiry = null;
let adminRefreshToken = null;
let adminRefreshTokenExpiry = null;

const getAdminToken = () => {
  return adminToken;
};

const getTokenExpiry = () => {
  return adminTokenExpiry;
};

const getRefreshToken = () => {
  return adminRefreshToken;
};

const isTokenExpired = () => {
  const expiry = getTokenExpiry();
  if (!expiry) return true;

  const expiryDate = new Date(expiry);
  const now = new Date();

  const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
  return expiryDate.getTime() - now.getTime() <= bufferTime;
};

const storeTokenData = (tokenData) => {
  if (tokenData?.access?.token) {
    adminToken = tokenData.access.token;
    adminTokenExpiry = tokenData.access.expires;
  }

  if (tokenData?.refresh?.token) {
    adminRefreshToken = tokenData.refresh.token;
    adminRefreshTokenExpiry = tokenData.refresh.expires;
  }
};

const clearTokenData = () => {
  adminToken = null;
  adminTokenExpiry = null;
  adminRefreshToken = null;
  adminRefreshTokenExpiry = null;
};

gasFeeAxios.interceptors.request.use(
  async (config) => {
    try {
      // Check if we need to refresh the token
      if (isTokenExpired()) {
        console.log("Token expired or expiring soon, attempting refresh...");
        await refreshTokenIfNeeded();
      }

      const token = getAdminToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn("No admin token found in localStorage");
      }
    } catch (error) {
      console.error("Error in request interceptor:", error);
      // If token refresh fails, clear all token data
      clearTokenData();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

gasFeeAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.error(
      "Gas Fee API Error:",
      error.response?.status,
      error.response?.data
    );

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await refreshTokenIfNeeded();

        // Retry the original request with new token
        const newToken = getAdminToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return gasFeeAxios(originalRequest);
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        clearTokenData();
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401) {
      clearTokenData();
      console.warn(
        "Admin token expired and refresh failed, cleared from storage"
      );
    }

    return Promise.reject(error);
  }
);

// Token refresh functionality
const refreshTokenIfNeeded = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    const response = await axios.post(
      `${GAS_FEE_BASE_URL}/v1/auth/refresh-tokens`,
      {
        refreshToken: refreshToken,
      }
    );

    const data = response.data?.data;
    if (data?.tokens) {
      storeTokenData(data.tokens);
      console.log("Token refreshed successfully");
      return data.tokens;
    } else {
      throw new Error("Invalid refresh response format");
    }
  } catch (error) {
    console.error("Token refresh failed:", error);
    clearTokenData();
    throw error;
  }
};

export const GasFeeRepository = {
  adminLogin: async (credentials) => {
    const url = "/v1/auth/login-admin";
    try {
      const response = await gasFeeAxios.post(url, credentials);
      const data = response.data || {};
      const nestedData = data.data || {};

      const tokens = nestedData.tokens;
      console.log("Login response tokens:", tokens);

      if (tokens) {
        storeTokenData(tokens);
        console.log("Tokens stored successfully");
      } else {
        console.error("No tokens found in login response:", response.data);
        console.error(
          "Available keys in response.data:",
          Object.keys(response.data)
        );
        if (response.data.data) {
          console.error(
            "Available keys in response.data.data:",
            Object.keys(response.data.data)
          );
        }
        throw new Error("No tokens received from login");
      }

      return response;
    } catch (error) {
      console.error("Admin login failed:", error);
      clearTokenData();
      throw error;
    }
  },

  // Function to clear admin token (for logout)
  adminLogout: () => {
    clearTokenData();
  },

  // Function to check if admin token exists and is valid
  hasAdminToken: () => {
    const token = getAdminToken();
    return !!token && !isTokenExpired();
  },

  // Function to manually refresh token
  refreshToken: async () => {
    return await refreshTokenIfNeeded();
  },

  getBalance: () => {
    const url = `/v1/disbursement/balance-of/?address=${ADMIN_WALLET_ADDRESS}`;
    return gasFeeAxios.get(url);
  },

  topUpWallet: () => {
    const url = `/v1/balance/${ADMIN_WALLET_ADDRESS}`;
    return gasFeeAxios.get(url);
  },
};

export default GasFeeRepository;
